<script setup lang="ts">
/**
 * This page display all data
 * created by the user,
 *
 * Can trigger the sync to see how this evolve to SUCCESS
 */
import { computed } from 'vue'
import { definePage } from 'unplugin-vue-router/runtime'
import PrimeButton from 'primevue/button'
import { useConfirm } from 'primevue/useconfirm'

import LayoutHeader from '@/layouts/header.vue'
import { useStoreAuth } from '@/stores/auth'
import { useStoreData } from '@/stores/data'
import { useStoreApp } from '@/stores/app'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import ButtonTranslate from '@/components/button-translate.vue'
import StatusOnline from '@/components/status-online.vue'

const { t } = useI18n()
const { logout } = useStoreAuth()
const router = useRouter()
const { dataState, syncData } = useStoreData()
const { appState } = useStoreApp()
const confirm = useConfirm()

definePage({
  meta: {
    requireAuth: true
  }
})

async function onClickLogout() {
  /**
   * TODO
   * First, check if we have data not yet synchronized.
   * If so, we ask the user if he agrees to remove them
   */
  if (dataUnsyncNb.value) {
    confirm.require({
      message: t('capel.routes.profile.logoutConfirm.message'),
      header: t('capel.routes.profile.logoutConfirm.header'),
      icon: 'pi pi-exclamation-circle',
      rejectLabel: t('capel.routes.profile.logoutConfirm.rejectLabel'),
      rejectProps: {
        label: t('capel.routes.profile.logoutConfirm.rejectLabel'),
        severity: 'secondary',
        outlined: true
      },
      acceptProps: {
        label: t('capel.routes.profile.logoutConfirm.acceptLabel'),
        severity: 'danger'
      },
      async accept() {
        await logout()
        router.push('/')
      }
    })
  } else {
    await logout()
    router.push('/')
  }
}

function groupName(groupId: string) {
  return dataState.groups.find((g) => g.id === groupId)?.name || t('capel.routes.profile.noGroup')
}

const dataUnsyncNb = computed(() => {
  let result = 0
  for (const groupId in dataState.offlineDataUser) {
    const records = dataState.offlineDataUser[groupId]
    result += records.length
  }
  return result
})
</script>

<template>
  <layout-header :display-menu-button="false">
    <main class="w-full max-w-4xl mx-auto mb-4 p-2 md:p-4">
      <h1 class="text-3xl my-8 text-center text-primary">
        {{ t('capel.routes.profile.title') }}
      </h1>

      <section
        class="mb-4 p-4 bg-white dark:bg-slate-800 border rounded-md text-center md:shadow-md"
      >
        <h3 class="text-xl">
          {{ t('capel.routes.profile.pref') }}
        </h3>

        <table class="text-left">
          <tbody>
            <tr class="h-12">
              <td class="w-1/2 md:w-64">
                {{ t('capel.routes.profile.lang') }}
              </td>
              <td>
                <button-translate />
              </td>
            </tr>
            <tr class="h-12">
              <td class="w-1/2 md:w-64">
                Application
              </td>
              <td>
                <status-online :online="appState.isOnline" />
              </td>
            </tr>
            <tr class="h-12">
              <td class="w-1/2 md:w-64">
                {{ t('capel.routes.profile.dataUnsyncNb') }}
              </td>
              <td>
                {{ dataUnsyncNb }}
              </td>
            </tr>
          </tbody>
        </table>
        <prime-button
          class="p-1 px-2 bg-primary text-white rounded-md"
          @click="onClickLogout"
        >
          {{ t('capel.routes.profile.logoutButtonLabel') }}
        </prime-button>
      </section>

      <section class="mb-4 p-4 bg-white dark:bg-slate-800 border rounded-md md:shadow-md">
        <h3 class="text-xl flex justify-between items-center">
          {{ t('capel.routes.profile.sync') }}

          <prime-button
            :disabled="dataState.syncing || !dataUnsyncNb"
            size="small"
            severity="primary"
            class="bg-primary hover:bg-primary-dark text-white border rounded px-3 py-2 rounded-md"
            @click="syncData"
          >
            {{ dataState.syncing ? 'Synchronisation en cours...' : 'Démarrer synchronisation' }}
          </prime-button>
        </h3>

        <template v-for="(records, groupId) in dataState.offlineDataUser">
          <div class="p-2 md:p-4 my-4">
            <h4 class="text-lg mb-2">
              Groupe {{ groupName(groupId) }}
            </h4>

            <table class="border w-full text-left">
              <tr>
                <th class="p-1">
                  Donnée
                </th>
                <th class="p-1">
                  État
                </th>
                <th class="p-1">
                  Action
                </th>
              </tr>
              <tr
                v-for="r in records"
                class="border hover:bg-gray-200"
              >
                <td class="p-1">
                  {{ t(r.tableTranslationKey) }} en date du
                  {{ r.originalData?.data?.['7de25c38-90c7-4565-a242-fa632e97b8b7'] }}
                </td>
                <td class="p-1">
                  {{ r.status }}
                </td>
                <td class="p-1">
                  {{ r.action }}
                </td>
              </tr>
            </table>
          </div>
        </template>

        <div class="flex justify-center my-2" />
      </section>
    </main>
  </layout-header>
</template>
